import { differenceInYears } from "date-fns/differenceInYears";
import { ArrowLeft, LogOut, Menu } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";

import { useGetPatientById } from "@elation/api-client/src/stitch-web";

import { IS_IN_IFRAME } from "@src/env";
import { logoutAndReloadToLogin } from "@src/features/auth";
import { queryClient } from "@src/queryClient";
import { PRODUCT_NAME } from "@src/shared/brand";
import { HeaderText } from "@src/shared/components/general/HeaderText";
import { Text } from "@src/shared/components/general/Text";
import { HorizontalFlexbox } from "@src/shared/components/layout/Flexbox";
import { Button } from "@src/shared/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@src/shared/components/ui/dialog";
import { navigation } from "@src/shared/navigation";

export interface HeaderProps {
  onMenuToggle: () => void;
  isChromeExtension: boolean;
  showMenuToggle?: boolean;
}

/**
 * Primary dashboard header. Shows a menu toggle, current patient banner (if
 * we're on a patient route), and a logout action (hidden when embedded in a
 * host iframe, where the host owns the session).
 */
export const Header = ({
  onMenuToggle,
  isChromeExtension,
  showMenuToggle = true,
}: HeaderProps) => {
  const navigate = useNavigate();
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  // Hide logout in any host-embedded iframe: the embedding host owns the
  // session, so a duplicate logout here would be confusing. Intentionally the
  // broad IS_IN_IFRAME, not useIsInElationEHR() — this applies to every
  // non-extension embed, not just Elation.
  const showLogout = !IS_IN_IFRAME;

  const params = useParams();
  const patientId = params.patientId;

  const { data: patientData } = useGetPatientById(Number(patientId), {
    query: {
      enabled: !!patientId,
    },
  });

  if (isChromeExtension) {
    return null;
  }

  // In any host-embedded iframe (e.g. the Elation EHR) the host owns the top
  // chrome, so drop our header row entirely rather than showing a redundant
  // "Stitch" bar.
  if (IS_IN_IFRAME) {
    return null;
  }

  const handleConfirmLogout = async () => {
    if (isLoggingOut) {
      return;
    }

    setIsLoggingOut(true);
    await logoutAndReloadToLogin(queryClient);
  };

  return (
    <HorizontalFlexbox
      as="header"
      alignItems="center"
      justifyContent="space-between"
      gap="md"
      className="sticky top-0 z-20 h-16 shrink-0 border-b border-border bg-background/90 px-4 backdrop-blur-md md-stitch:px-6"
    >
      <HorizontalFlexbox alignItems="center" gap="sm">
        {showMenuToggle ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={onMenuToggle}
            aria-label="Toggle sidebar"
          >
            <Menu />
          </Button>
        ) : null}
        {patientId ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(navigation.patients())}
            aria-label="Back to patient list"
            className="gap-1"
          >
            <ArrowLeft className="size-4" />
            <span className="hidden sm-stitch:inline">Patients</span>
          </Button>
        ) : null}
        {patientData?.data ? (
          <HorizontalFlexbox alignItems="center" gap="sm">
            <HeaderText tag="h1" className="text-lg">
              {patientData.data.firstName} {patientData.data.lastName}
            </HeaderText>
            <span
              aria-hidden
              className="hidden h-5 w-px bg-border sm-stitch:block"
            />
            <Text
              as="span"
              sizing="sm"
              appearance="subdued"
              className="hidden sm-stitch:inline"
            >
              {differenceInYears(new Date(), new Date(patientData.data.dob))}
              {patientData.data.sex.charAt(0).toUpperCase()}
            </Text>
          </HorizontalFlexbox>
        ) : (
          <HeaderText tag="h1" className="text-lg">
            {PRODUCT_NAME.singular}
          </HeaderText>
        )}
      </HorizontalFlexbox>
      <HorizontalFlexbox alignItems="center" gap="xs">
        {showLogout ? (
          <Dialog
            open={isLogoutDialogOpen}
            onOpenChange={(open) => {
              if (!isLoggingOut) {
                setIsLogoutDialogOpen(open);
              }
            }}
          >
            <DialogTrigger asChild>
              <Button size="sm">
                <LogOut className="mr-1 size-4" />
                Logout
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Log out?</DialogTitle>
                <DialogDescription>
                  You will be signed out and returned to the login page.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline" disabled={isLoggingOut}>
                    Cancel
                  </Button>
                </DialogClose>
                <Button
                  variant="destructive"
                  onClick={handleConfirmLogout}
                  disabled={isLoggingOut}
                >
                  {isLoggingOut ? "Logging out..." : "Log out"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        ) : null}
      </HorizontalFlexbox>
    </HorizontalFlexbox>
  );
};
